import { Button } from "@material-ui/core";
import { DateRange } from "@material-ui/icons";
import { FluxoMonitor } from "common/genericTypes";
import { Section, SmSpace } from "m2m.components";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store";
import { updateMetadata } from "store/modules/notaComercial/caracteristicas";
import { localizedTextsMap } from "utils/dataGridUtils";
import { formatNumber } from "utils/number";
import { useGerarRequest } from "../useGerarRequest";
import {
  updateAmortizacoes,
  updateDatasPagamentoJuros,
} from 'store/modules/notaComercial/eventos';
import { useValidarCampos } from "../useValidarCampos";
import { DataGridPro, GridColumns, GridRowsProp, GridValueFormatterParams } from "@mui/x-data-grid-pro";
import { EtapaCadastro } from "common/registro/enums";

const FluxoMonitorSection: React.FC = () => {

  const [rows, setRows] = useState<GridRowsProp>([]);
  const [pageSize, setPageSize] = useState<number>(20);

  const dispatch = useDispatch();
  const postGerarFluxo = useSelector((state: RootState) => state.instrumentoApi.postGerarFluxoMonitor);
  const fluxo = useSelector((state: RootState) => state.notaComercialCadastro.metadata.fluxoMonitor);
  const amortizacoes = useSelector((state: RootState) => state.notaComercialCadastro.eventos.amortizacoes);
  const pagamentoJuros = useSelector((state: RootState) => state.notaComercialCadastro.eventos.datasPagamentoJuros);

  const instrumento = useGerarRequest();
  const camposInvalidosCaracteristicas = useValidarCampos(EtapaCadastro.Caracteristicas);
  const camposInvalidosCriteriosRem = useValidarCampos(EtapaCadastro.CriteriosRemuneracao);

  const getGridColumns: GridColumns = [
    {
      field: 'data',
      headerName: 'Data',
      width: 160,
      type: 'date',
      valueFormatter: (params: GridValueFormatterParams<Date>) => {
        return !params.value ? '' : moment(params.value).format('DD/MM/YYYY');
      }
    },
    {
      field: 'principal',
      headerName: 'Principal',
      type: 'number',
      width: 180,
      valueFormatter: (params: GridValueFormatterParams<number>) => {
        return !params.value ? '' : formatNumber(params.value, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      },
    },
    {
      field: 'juros',
      headerName: 'Juros',
      type: 'number',
      width: 180,
      valueFormatter: (params: GridValueFormatterParams<number>) => {
        return !params.value ? '' : formatNumber(params.value, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      },
    },
    {
      field: 'valorAquisicao',
      headerName: 'Valor Aquisição',
      type: 'number',
      width: 180,
      valueFormatter: (params: GridValueFormatterParams<number>) => {
        return !params.value ? '' : formatNumber(params.value, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      },
    },
    {
      field: 'valorParcela',
      headerName: 'Valor da Parcela',
      type: 'number',
      width: 180,
      valueFormatter: (params: GridValueFormatterParams<number>) => {
        return !params.value ? '' : formatNumber(params.value, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      },
    },
  ];

  const gerarFluxo = useCallback(async () => {
    try {
      const result = await postGerarFluxo(dispatch, "NOTA_COMERCIAL", instrumento, true);
      const fluxoMapeado = result.data.map((fluxo, index): FluxoMonitor => {
        return {
          id: index.toString(), data: fluxo.dataDeVencimento,
          principal: fluxo.amortizacao, juros: fluxo.juros,
          valorAquisicao: fluxo.valorAquisicao, valorParcela: fluxo.total,
          percentualAmortizacao: fluxo.percentualAmortizacao
        };
      });
      dispatch(updateMetadata({ name: 'fluxoMonitor', value: fluxoMapeado }));

      if (!amortizacoes.length && (!pagamentoJuros || !pagamentoJuros.length)) {
        dispatch(updateAmortizacoes(fluxoMapeado.filter(f => f.percentualAmortizacao > 0)
          .map(f => ({ id: f.id, dataAmortizacao: f.data, percentualAmortizacao: f.percentualAmortizacao * 100 }))));
        dispatch(updateDatasPagamentoJuros(fluxoMapeado.filter(f => f.juros > 0).map(f => ({ id: f.id, date: f.data }))));
      }

    } catch { }
  }, [instrumento, amortizacoes, pagamentoJuros]);

  const tableHeight = () => {
    if (rows.length <= 3) return 300;
    if (rows.length <= 6) return 600;
    return 800;
  }

  useEffect(() => {
    setRows(fluxo);
  }, [fluxo]);

  return (
    <Section title="Fluxo de Pagamento">
      <Button
        disabled={!!(camposInvalidosCaracteristicas.length || camposInvalidosCriteriosRem.length)}
        startIcon={<DateRange />} variant="contained" color="primary" size="small" onClick={gerarFluxo}
      >
        {"Gerar Fluxo"}
      </Button>
      <SmSpace />
      {rows.length > 0 && <div style={{ height: tableHeight(), width: 900 }}>
        <DataGridPro
          rows={rows}
          columns={getGridColumns}
          editMode="row"
          pageSize={pageSize}
          localeText={localizedTextsMap}
        />
      </div>}
    </Section>
  );
}

export default FluxoMonitorSection;